/**
 * Sub-page copy, transcribed from the Figma frames.
 *
 * Two content decisions are recorded in docs/CONTENT-GAPS.md:
 *  - FAQ answers: only "How much does it cost?" was written in the comp. The
 *    other eight are drafted here from the dev-branch product behaviour and
 *    need sign-off before launch.
 *  - Product Tour: the comp's heading says "Six screens" but shows five tabs,
 *    and its fifth frame duplicates Property Owner instead of Event Dashboard.
 *    Event Dashboard copy is drafted; the heading is corrected to "Five screens".
 */

export const solutions = {
  eyebrow: 'Solutions',
  title: 'Built for how each property actually operates',
  subtitle:
    'From daily valet operations to large-scale events, NimblePark adapts to your property, team, and workflow.',
  items: [
    {
      id: 'hotels',
      n: 1,
      title: 'Hotels',
      points: ['Guest valet operations', 'VIP guest handling', 'Banquet parking', 'Vehicle tracking'],
    },
    { id: 'malls', n: 2, title: 'Malls', points: ['Peak hour traffic', 'Customer parking', 'Valet services'] },
    {
      id: 'events',
      n: 3,
      title: 'Events',
      points: ['Wedding parking', 'Corporate events', 'Exhibitions', 'Temporary workforce'],
    },
    {
      id: 'vsp',
      n: 4,
      title: 'VSP',
      points: [
        'Staff management',
        'Client management',
        'Operations dashboard',
        'Regular & temporary premises',
      ],
    },
  ],
};

export const userRoles = {
  eyebrow: 'User Roles',
  title: 'Explore NimblePark by Role',
  subtitle: 'See how every team member uses NimblePark to manage parking operations faster and more efficiently.',
  items: [
    { n: 1, title: 'Customer', points: ['Track vehicle', 'Request retrieval', 'Receive notifications'] },
    { n: 2, title: 'Valet', points: ['Check-in vehicles', 'Update parking status', 'Manage retrieval'] },
    { n: 3, title: 'Supervisor', points: ['Assign valets', 'Monitor operations'] },
    { n: 4, title: 'Property Owner', points: ['Monitor parking', 'View reports', 'Manage locations'] },
    { n: 5, title: 'Event Manager', points: ['Temporary parking setup', 'Staff deployment', 'Live operations'] },
    {
      n: 6,
      title: 'Valet Service Provider',
      points: ['Manage multiple clients', 'Manage workforce', 'Manage requests from event managers'],
    },
  ],
};

export const productTour = {
  eyebrow: 'Product Tour',
  // Comp says "Six screens" but shows five tabs. Corrected to match reality.
  title: 'Five screens, one live ticket',
  subtitle:
    'Every app in the NimblePark suite reads from the same ticket — so a status change ' +
    'on the valet’s phone appears instantly on the supervisor’s dashboard.',
  tabs: [
    {
      id: 'customer',
      label: 'Customer App',
      title: 'Customer App',
      body:
        'Guests skip the paper stub entirely. They watch their vehicle’s status change from ' +
        'the app and request retrieval before they’ve even finished their coffee.',
      points: ['Track vehicle', 'Request retrieval', 'Receive notifications'],
    },
    {
      id: 'valet',
      label: 'Valet App',
      title: 'Valet App',
      body:
        'A single queue tells every valet exactly what’s next — check a vehicle in, log the bay ' +
        'it’s parked in, or head out for a retrieval, all logged to the second.',
      points: ['Check-in vehicles', 'Update parking status', 'Manage retrieval'],
    },
    {
      id: 'supervisor',
      label: 'Supervisor Dashboard',
      title: 'Supervisor Dashboard',
      body:
        'See the whole floor at a glance, assign the closest free valet to a new request, and ' +
        'catch a slow retrieval before a guest has to ask about it.',
      points: ['Assign valets', 'Monitor operations', 'Rebalance staff live'],
    },
    {
      id: 'owner',
      label: 'Property Owner Dashboard',
      title: 'Property Owner Dashboard',
      body:
        'Whether it’s one location or twelve, owners get one report — retrieval times, staff ' +
        'performance, and volume trends — without calling each site manager.',
      points: ['Monitor parking', 'View reports', 'Manage locations'],
    },
    {
      id: 'event',
      label: 'Event Dashboard',
      // DRAFTED — the comp's fifth frame duplicates Property Owner. See docs/CONTENT-GAPS.md.
      title: 'Event Dashboard',
      body:
        'Stand up a temporary operation for a single evening: define the lot, bring on extra ' +
        'valets for the shift, and watch arrivals and retrievals peak in real time.',
      points: ['Temporary parking setup', 'Staff deployment', 'Live operations'],
    },
  ],
};

export const industries = {
  eyebrow: 'Industries We Serve',
  title: 'Wherever cars queue up, we fit in',
  subtitle:
    'Nimblepark fits seamlessly into any operation, helping you manage vehicles smarter and ' +
    'deliver better experiences.',
  items: [
    {
      id: 'hotels',
      title: 'Hotels',
      body: 'Arrival-to-checkout valet for guests who never want to think about their car.',
      image: 'industry-hotels',
    },
    {
      id: 'malls',
      title: 'Malls',
      body: 'High-volume, multi-level parking with real-time bay tracking.',
      image: 'industry-malls',
    },
    {
      id: 'restaurants',
      title: 'Restaurants',
      body: 'Fast turnaround valet that keeps the entrance clear during peak hours.',
      image: 'industry-restaurants',
    },
    {
      id: 'convention',
      title: 'Convention Centers',
      body: 'Scale valet operations up for a 5,000-attendee day and back down after.',
      image: 'industry-convention',
    },
  ],
};

export const benefitsCalculator = {
  eyebrow: 'Benefits Calculator',
  title: 'Estimate what NimblePark saves your property',
  subtitle: 'A rough, directional estimate based on typical improvements — actual results vary by site.',
  inputs: [
    { id: 'vehicles', label: 'Vehicles Managed Daily', unit: 'Vehicles / day', min: 20, max: 1000, step: 10, initial: 270 },
    { id: 'retrieval', label: 'Current retrieval time (minutes)', unit: 'Minutes / vehicle', min: 3, max: 30, step: 1, initial: 12 },
    { id: 'staff', label: 'Current staff count', unit: 'Valets on shift', min: 1, max: 60, step: 1, initial: 10 },
  ],
};

/**
 * Only question 02 had an answer in the comp. The other eight are drafted from
 * the dev-branch product (roles, OTP retrieval, multi-hotel models) and are
 * flagged for sign-off in docs/CONTENT-GAPS.md.
 */
export const faqs = {
  eyebrow: 'FAQs',
  title: 'Frequently Asked Questions',
  subtitle:
    'Find answers to common questions about NimblePark, its features, and services. Learn how our ' +
    'platform simplifies valet operations for businesses of all sizes.',
  items: [
    {
      q: 'What is NimblePark?',
      a: 'NimblePark is a valet and parking management platform. It replaces paper tickets and radio calls with a digital ticket that follows the vehicle from check-in to retrieval, and gives supervisors and owners a live view of the whole operation.',
      draft: true,
    },
    {
      q: 'How much does it cost?',
      a: 'Pricing depends on property type, vehicle volume, and the number of locations. Book a demo and we’ll put together a plan based on your operation.',
    },
    {
      q: 'Can it be customized?',
      a: 'Yes. Floors, parking slots, services and staff roles are all configured per property, so the app matches how your site already runs rather than forcing a fixed workflow.',
      draft: true,
    },
    {
      q: 'Do customers need to install an app?',
      a: 'No. Guests receive a digital ticket they can open in any browser, and retrieval is confirmed with a one-time password sent to their phone. The app is optional, not required.',
      draft: true,
    },
    {
      q: 'Can multiple properties be managed?',
      a: 'Yes. A single owner account covers every site, with per-property dashboards and combined reporting across all of them.',
      draft: true,
    },
    {
      q: 'Can temporary staff be added?',
      a: 'Yes. Valets can be added for a single shift or event and removed afterwards, without affecting your permanent roster.',
      draft: true,
    },
    {
      q: 'Can I use it for events?',
      a: 'Yes. Event mode lets you set up a temporary lot, deploy staff for the day, and track arrivals and retrievals live, then wind the operation back down afterwards.',
      draft: true,
    },
    {
      q: 'Can I use my own valet team?',
      a: 'Yes. NimblePark is the software layer — you keep your existing team, or bring in a valet service provider who manages staffing through the same platform.',
      draft: true,
    },
    {
      q: 'How long does implementation take?',
      a: 'A single property is typically live within a few days: configure floors and slots, add your staff, and run a shift alongside your current process before switching over.',
      draft: true,
    },
  ],
};
